import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.webp';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './PlantEquipmentRegisterSoftware.css';

const TRACKABLE_ITEMS = [
  { text: 'Vehicles', icon: 'vehicle' },
  { text: 'Plant', icon: 'plant' },
  { text: 'Equipment', icon: 'equipment' },
  { text: 'Tools', icon: 'tool' },
  { text: 'Registration details and expiry', icon: 'calendar' },
  { text: 'Service intervals and service due dates', icon: 'service' },
  { text: 'Odometer readings', icon: 'gauge' },
  { text: 'Engine hours', icon: 'clock' },
  { text: 'Asset condition', icon: 'condition' },
  { text: 'Asset status', icon: 'status' },
  { text: 'Asset location', icon: 'pin' },
  { text: 'Assigned workers', icon: 'users' },
  { text: 'Manufacturer, model and serial number', icon: 'tag' },
];

const WORKFLOW_STEPS = [
  {
    title: 'Create an asset record',
    icon: 'equipment',
    text: 'Add the vehicle, plant, equipment or tool to the register with its identifying information, including name, type, manufacturer, model and serial number.',
  },
  {
    title: 'Add compliance and service information',
    icon: 'service',
    text: 'Record relevant registration details, expiry dates, odometer or engine-hour information, service intervals and service due dates.',
  },
  {
    title: 'Record condition and status',
    icon: 'condition',
    text: 'Keep the current condition, status and location of the asset within its record.',
  },
  {
    title: 'Assign the asset',
    icon: 'users',
    text: 'Link an asset to an employee where responsibility or custody needs to be recorded.',
  },
  {
    title: 'Search and review records',
    icon: 'search',
    text: 'Use the asset register to find records and review equipment information when needed rather than searching through multiple spreadsheets or files.',
  },
];

const AUDIENCE = [
  {
    title: 'Construction businesses',
    text: 'managing plant, vehicles and site equipment',
    icon: 'hardhat',
  },
  {
    title: 'Fleet and plant managers',
    text: 'tracking service and registration information',
    icon: 'vehicle',
  },
  {
    title: 'Operations teams',
    text: 'managing vehicles and equipment across locations',
    icon: 'dashboard',
  },
  {
    title: 'Supervisors',
    text: 'assigning tools or equipment to workers',
    icon: 'users',
  },
  {
    title: 'Compliance and quality teams',
    text: 'maintaining structured asset evidence',
    icon: 'shield',
  },
];

const SPEC_TABLE = [
  { capability: 'Asset types', value: 'Vehicles, plant, equipment and tools' },
  { capability: 'Identification', value: 'ID, name, type, manufacturer, model, serial number' },
  { capability: 'Vehicle information', value: 'Registration number and expiry' },
  { capability: 'Usage records', value: 'Odometer and engine hours' },
  { capability: 'Maintenance', value: 'Service intervals and service due dates' },
  { capability: 'Asset status', value: 'Status and condition' },
  { capability: 'Location', value: 'Asset location' },
  { capability: 'Assignment', value: 'Link assets to employees' },
  { capability: 'Organisation', value: 'Search and pagination' },
  { capability: 'Platform', value: 'Web platform with mobile apps' },
  { capability: 'Broader compliance', value: 'Documents, forms, sites, projects and tasks' },
];

const BENEFITS = [
  'One structured register for plant and equipment',
  'Easier access to service and registration information',
  'Clearer asset ownership and worker assignments',
  'Asset records that can support audit and compliance evidence',
  'Less dependence on scattered spreadsheets',
  "Useful alongside Complimate's wider WHS management features",
];

const FAQ_ITEMS = [
  {
    question: 'What is plant and equipment register software?',
    answer:
      'It is software used to maintain digital records for workplace plant, vehicles, equipment and tools, including details such as service dates, registration expiry, condition, location and assignments.',
  },
  {
    question: 'Can Complimate track equipment service dates?',
    answer:
      'Yes. The asset register includes service intervals and service due information for vehicles, plant, equipment and tools.',
  },
  {
    question: 'Can I track vehicle registration expiry?',
    answer:
      'Yes. Vehicle records can include registration information and expiry dates, helping teams keep registration details within the asset record.',
  },
  {
    question: 'Can I assign equipment to workers?',
    answer:
      'Yes. Complimate supports linking assets to employees, which can help businesses maintain clearer responsibility and custody records.',
  },
  {
    question: 'Is Complimate suitable for construction businesses?',
    answer:
      "Yes. The asset register specifically supports plant and equipment records, and the platform's product positioning includes construction among its target industries.",
  },
  {
    question: 'Can I use Complimate on mobile?',
    answer:
      'Complimate has Android and iOS mobile apps that provide access to core compliance functionality in the field, including documents, tasks, forms and photo evidence.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/plant-equipment-register-software/');

const ICON_PATHS = {
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  vehicle: (
    <>
      <path d="M5 11 7 6h10l2 5" />
      <path d="M4 11h16v7H4v-7Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
      <path d="M7 14h.01M17 14h.01" />
    </>
  ),
  equipment: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <path d="M8 5V3h8v2M4 11h16M9 11v2h6v-2" />
    </>
  ),
  tool: (
    <>
      <path d="M14.5 6.5a4 4 0 0 0-5 5L3 18l3 3 6.5-6.5a4 4 0 0 0 5-5l-2.5 2.5-3-3 2.5-2.5Z" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18M8 14h3M8 17h6" />
    </>
  ),
  service: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </>
  ),
  gauge: (
    <>
      <path d="M4 17a8 8 0 1 1 16 0" />
      <path d="m12 13 4-4M6 17h12" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  condition: (
    <>
      <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />
      <path d="m8.5 12 2.2 2.2 4.8-5" />
    </>
  ),
  status: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 2.5 2.5L16 9" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  tag: (
    <>
      <path d="M3 4h8l10 10-7 7L4 11V4Z" />
      <circle cx="8" cy="8" r="1.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  hardhat: (
    <>
      <path d="M4 14a8 8 0 0 1 16 0M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3ZM12 4v3" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  spreadsheet: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 4v16M15 9v11" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="per-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.plant}
      </svg>
    </span>
  );
}

function PlantEquipmentRegisterSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="per-page">
        <section className="per-hero">
          <Container>
            <div className="per-hero-grid">
              <div className="per-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="plant" />
                  Plant &amp; Equipment Register Software
                </p>
                <h1 className="seo-h1">Plant &amp; Equipment Register Software</h1>
                <p className="seo-lead">
                  Complimate&apos;s plant and equipment register software helps
                  Australian businesses keep records for vehicles, plant, equipment
                  and tools in one structured system. Track service intervals,
                  registration expiry, condition, location and assigned workers
                  instead of relying on disconnected spreadsheets.
                </p>
                <p className="seo-body">
                  It is designed for teams that need clearer asset records and
                  easier access to compliance information.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to={APP_URLS.SIGNUP}
                    className="btn-teal per-primary-button"
                    id="per_start_trial_hero"
                  >
                    Start Your Free Trial
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="per-hero-visual">
                <div className="per-visual-glow" aria-hidden="true" />
                <div className="per-product-window">
                  <div className="per-window-bar" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate dashboard for plant, equipment, vehicle and tool records"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="per-float-card per-float-card-top" aria-hidden="true">
                  <FeatureIcon name="service" />
                  <span>Service due dates</span>
                </div>
                <div className="per-float-card per-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="calendar" />
                  <span>Registration expiry</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section per-intro-section">
          <Container>
            <div className="per-intro-grid">
              <div>
                <div className="per-section-marker"><FeatureIcon name="equipment" /></div>
                <h2 className="seo-h2">
                  What Is Plant &amp; Equipment Register Software?
                </h2>
                <p className="seo-body">
                  Plant and equipment register software is a digital system for
                  recording and managing business assets such as vehicles,
                  machinery, plant, equipment and tools.
                </p>
                <p className="seo-body">
                  Instead of maintaining separate spreadsheets or paper records,
                  Complimate provides a central asset register where authorised
                  users can search asset records and review important details
                  such as service due dates, registration expiry, condition,
                  location and who an asset is assigned to.
                </p>
                <p className="seo-body mb-0">
                  This can be useful for construction businesses, operations
                  teams, fleet managers, supervisors and compliance teams that
                  need a structured record of workplace assets.
                </p>
              </div>
              <div className="per-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate central register for asset and compliance records"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section per-track-section">
          <Container>
            <div className="per-section-intro">
              <h2 className="seo-h2">What Can You Track With Complimate?</h2>
              <p className="seo-body">
                Complimate&apos;s asset management and equipment register
                supports records for:
              </p>
            </div>
            <ul className="per-track-grid">
              {TRACKABLE_ITEMS.map((item) => (
                <li className="per-track-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <div className="per-note">
              <FeatureIcon name="users" />
              <p className="seo-body mb-0">
                The asset module also supports linking assets to employees,
                making it easier to see who is responsible for particular
                equipment or tools.
              </p>
            </div>
          </Container>
        </section>

        <section className="seo-section per-workflow-section">
          <Container>
            <div className="per-section-intro">
              <h2 className="seo-h2">
                How Does the Plant &amp; Equipment Register Work?
              </h2>
              <p className="seo-body">
                The register is designed around the information teams need to
                manage assets in day-to-day operations.
              </p>
            </div>
            <div className="per-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="per-step">
                  <div className="per-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">{step.title}</h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section per-spreadsheet-section">
          <Container>
            <div className="per-spreadsheet-grid">
              <div className="per-shot-frame">
                <img
                  src={isDark ? DarkManageUser : LightManageUser}
                  alt="Complimate structured employee and asset assignment records"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="per-section-marker"><FeatureIcon name="spreadsheet" /></div>
                <h2 className="seo-h2">
                  Why Use Asset Management Software Instead of a Spreadsheet?
                </h2>
                <p className="seo-body">
                  A spreadsheet can record basic equipment information, but it can
                  become difficult to maintain as the number of assets grows.
                </p>
                <p className="seo-body">
                  Complimate gives businesses a structured register with defined
                  asset fields, search and pagination, service information, expiry
                  details, condition, location and employee assignments. This makes
                  the register more useful for operational and compliance workflows.
                </p>
                <p className="seo-body mb-0">
                  For example, a plant manager can use the register to review
                  equipment records, while an operations team can check vehicle
                  registration and service information. Supervisors can also use
                  asset assignments to maintain clearer custody records for tools
                  and equipment.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section per-audience-section">
          <Container>
            <div className="per-section-intro">
              <h2 className="seo-h2">
                Who Is Complimate Plant &amp; Equipment Register Software For?
              </h2>
              <p className="seo-body">
                The asset register can support businesses and teams that manage
                workplace equipment, including:
              </p>
            </div>
            <ul className="per-audience-grid">
              {AUDIENCE.map((item) => (
                <li className="per-audience-card" key={item.title}>
                  <FeatureIcon name={item.icon} />
                  <span>
                    <strong>{item.title}</strong> — {item.text}
                  </span>
                </li>
              ))}
            </ul>
            <p className="seo-body per-centered-note">
              Complimate&apos;s broader platform also includes sites, employees,
              digital forms, documents, projects and corrective actions,
              allowing asset information to sit alongside other compliance
              records.
            </p>
          </Container>
        </section>

        <section className="seo-section per-cta-section">
          <Container>
            <div className="per-cta-card">
              <span className="per-cta-orb per-cta-orb-one" aria-hidden="true" />
              <span className="per-cta-orb per-cta-orb-two" aria-hidden="true" />
              <div className="per-cta-copy">
                <h2 className="seo-h2">Manage Plant &amp; Equipment in One Register</h2>
                <p className="seo-body">
                  Keep your vehicles, plant, equipment and tools organised with
                  Complimate&apos;s plant and equipment register software. Track key
                  asset information, service requirements, expiry details,
                  condition, location and assignments within your broader compliance
                  platform.
                </p>
              </div>
              <Button
                as={Link}
                to={APP_URLS.SIGNUP}
                className="btn-teal per-cta-button"
                id="per_start_trial_mid"
              >
                Start Your Free Trial
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section per-spec-section">
          <Container>
            <div className="per-section-intro">
              <h2 className="seo-h2">
                Plant &amp; Equipment Register Software: Key Specifications
              </h2>
            </div>
            <div className="seo-table-wrap per-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Capability</th>
                    <th scope="col">Complimate</th>
                  </tr>
                </thead>
                <tbody>
                  {SPEC_TABLE.map((row) => (
                    <tr key={row.capability}>
                      <th scope="row">{row.capability}</th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              The functionality brief identifies the asset module as part of
              Complimate&apos;s live product functionality, with the brief last
              updated in September 2026.
            </p>
          </Container>
        </section>

        <section className="seo-section per-benefits-section">
          <Container>
            <div className="per-benefit-layout">
              <div className="per-benefit-panel">
                <div className="per-section-marker"><FeatureIcon name="status" /></div>
                <h2 className="seo-h2">Potential Benefits</h2>
                <ul className="per-benefit-list">
                  {BENEFITS.map((item) => (
                    <li key={item}>
                      <span className="per-check" aria-hidden="true">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="per-limit-panel">
                <div className="per-section-marker"><FeatureIcon name="info" /></div>
                <h2 className="seo-h2">Limitations to Consider</h2>
                <p className="seo-body">
                  Complimate&apos;s asset register is a compliance and
                  operational asset register, not a specialist enterprise
                  fleet-management or telematics system. The documented
                  functionality focuses on asset records, service intervals,
                  expiry information, condition, location and assignments.
                </p>
                <p className="seo-body mb-0">
                  If you need advanced GPS fleet tracking, fuel management or
                  telematics, you should assess whether a dedicated
                  fleet-management system is also required.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section per-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default PlantEquipmentRegisterSoftware;
